import { useCallback, useEffect, useState } from "react";
import { Receipt, RefreshCw, Search, XCircle, Landmark, Mail } from "lucide-react";
import { toast } from "sonner";
import { CollapsibleSection } from "@/components/settings/SettingsUI";
import { getFoundationARQueue, foundationARDryRun, getFoundationARSupportRequest } from "@/lib/api";
import FoundationARPushButton from "./FoundationARPushButton";
import { ARTaxAuditBadge } from "./ARTaxAuditBadge";
import { ARReplayPanel } from "./ARReplayPanel";

const fmt = (s) => (s ? String(s).slice(0, 10) : "—");
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const STATUS_CLS = { sent: "bg-sky-100 text-sky-800", paid: "bg-emerald-100 text-emerald-800", partial: "bg-amber-100 text-amber-800", draft: "bg-slate-100 text-slate-600" };

// AR Invoices awaiting Foundation — every finalized invoice not yet written
// to ar_invoice / ar_invoice_gl, with named blockers, regardless of the
// master switch. Direct bridge write only: no export, no file.
export default function FoundationARQueue({ onCountChange }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dryRunning, setDryRunning] = useState(null);
  const [dryRuns, setDryRuns] = useState({});

  const runDryRun = async (invoiceId) => {
    setDryRunning(invoiceId);
    try {
      const { data } = await foundationARDryRun(invoiceId);
      setDryRuns((prev) => ({ ...prev, [invoiceId]: data }));
      const failed = (data.steps || []).find((s) => s.ok === false);
      toast[failed ? "error" : "success"](failed ? `Dry run stopped at ${failed.step} — nothing written` : "Dry run completed — nothing written");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dry run failed");
    } finally {
      setDryRunning(null);
    }
  };
  const [loading, setLoading] = useState(false);
  const [copyingSupport, setCopyingSupport] = useState(null);
  const [supportText, setSupportText] = useState({});

  const copySupportRequest = async (invoiceId) => {
    setCopyingSupport(invoiceId);
    let data;
    try {
      ({ data } = await getFoundationARSupportRequest(invoiceId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build the support request");
      setCopyingSupport(null);
      return;
    }
    const text = `Subject: ${data.subject}\n\n${data.body}`;
    setSupportText((prev) => ({ ...prev, [invoiceId]: text }));
    try {
      await navigator.clipboard.writeText(text);
      toast.success(data.from_failure ? "Support request copied — built from the real failed attempt" : "Support request copied — no failed attempt logged yet, statements are the current preview");
    } catch {
      toast.message("Support request ready below — clipboard unavailable, select and copy it");
    } finally {
      setCopyingSupport(null);
    }
  };

  const reload = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data: d } = await getFoundationARQueue({ q: query || undefined, limit: 200 });
      setData(d);
      onCountChange?.(d.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load AR invoices awaiting Foundation");
    } finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => {
    const t = setTimeout(() => reload(q), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, reload]);

  const globalBlocker = data && (!data.ar_push_enabled
    ? "AR push disabled — master switch is OFF (Foundation Settings → AR). Rows are listed; pushing is inactive."
    : !data.bridge?.ok ? "Foundation bridge is offline."
      : !data.bridge?.capable ? `Bridge v${data.bridge.version} does not yet support AR writes — upgrade required.` : null);

  return (
    <CollapsibleSection
      id="foundation-ar-queue" testId="foundation-ar-queue"
      icon={Receipt} iconClass="text-emerald-600"
      title={`AR Invoices awaiting Foundation (${data ? data.total : "…"})`}
      actions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice #, WO #, customer"
                   className="pl-6 pr-2 py-1 text-xs border border-slate-300 rounded-md w-48" data-testid="foundation-ar-queue-search" />
          </div>
          <button onClick={() => reload(q)} disabled={loading} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="foundation-ar-queue-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      }
    >
      {globalBlocker && (
        <div className="mb-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-center gap-1.5" data-testid="foundation-ar-queue-global-blocker">
          <XCircle className="w-3.5 h-3.5 shrink-0" /> {globalBlocker}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mb-2">
        Direct write through the bridge into <code>ar_invoice</code> + <code>ar_invoice_gl</code> ({data?.database || "…"}) as unposted invoices — no export step. Push opens the column-by-column preview.
      </div>
      {data && data.rows.length > 0 && (
        <ARReplayPanel total={data.rows.length} disabled={!!globalBlocker} disabledReason={globalBlocker ? "Replay is unavailable until the blocker above is cleared." : ""} onDone={() => reload(q)} />
      )}
      {data && data.rows.length === 0 && <div className="text-xs text-slate-400 py-3" data-testid="foundation-ar-queue-empty">No finalized invoices waiting.</div>}
      <div className="space-y-2">
        {(data?.rows || []).map((row) => (
          <div key={row.invoice_id} className="rounded-md border border-slate-200 bg-white p-2.5" data-testid={`foundation-ar-row-${row.invoice_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold text-slate-800">{row.invoice_number}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_CLS[row.status] || "bg-slate-100 text-slate-600"}`}>{row.status}{row.is_final ? " · final" : ""}</span>
                  <span className="text-[11px] text-slate-500">WO {row.wo_number || "—"} · {row.customer_name || "—"}</span>
                  {row.foundation_ar_status && row.foundation_ar_status !== "success" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid={`foundation-ar-last-${row.invoice_id}`}>last attempt: {row.foundation_ar_status}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  invoice_amount {money(row.invoice_amount)} · tax {money(row.tax_amount)} · amount_due <strong>{money(row.amount_due)}</strong> · invoice_date {fmt(row.invoice_date)}
                </div>
                <ul className="mt-1 space-y-0.5" data-testid={`foundation-ar-blockers-${row.invoice_id}`}>
                  {row.blockers.map((b) => (
                    <li key={b.code} className="text-[11px] text-rose-700 flex items-start gap-1" data-testid={`foundation-ar-blocker-${row.invoice_id}-${b.code}`}>
                      <XCircle className="w-3 h-3 mt-0.5 shrink-0" /><span><span className="font-mono text-rose-500">{b.code}</span> — {b.message}</span>
                    </li>
                  ))}
                  {row.blockers.length === 0 && <li className="text-[11px] text-emerald-700 flex items-center gap-1"><Landmark className="w-3 h-3" /> All gates pass — ready to write</li>}
                </ul>
                <ARTaxAuditBadge invoiceId={row.invoice_id} audit={row.tax_audit} canWrite onFlagged={() => reload(q)} />
                {/* iter311 — the last rejection, in full. The SQL text used to
                    be clipped by this column, which hid the part of the message
                    that actually names the table and column Foundation refused. */}
                {row.last_error && (
                  <div className="mt-1.5 rounded-md border border-rose-200 bg-rose-50 p-1.5" data-testid={`foundation-ar-last-error-${row.invoice_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[11px] text-rose-900 break-words min-w-0">
                        <span className="font-semibold">Last attempt failed</span>
                        {row.last_error.at ? <span className="text-rose-500"> · {fmt(row.last_error.at)}</span> : null}
                        <div className="mt-0.5">{row.last_error.plain_error}</div>
                      </div>
                      {row.last_error.exception_text && (
                        <button
                          type="button"
                          className="shrink-0 h-6 px-1.5 text-[10px] rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-100"
                          onClick={() => {
                            navigator.clipboard?.writeText(
                              `${row.invoice_number}\n${row.last_error.exception_text}\n\nheader sent:\n${JSON.stringify(row.last_error.header_sent, null, 1)}\n\nGL line sent:\n${JSON.stringify(row.last_error.lines_sent, null, 1)}`
                            );
                            toast.success("Full error and the exact payload copied");
                          }}
                          data-testid={`foundation-ar-copy-error-${row.invoice_id}`}
                        >
                          Copy full error
                        </button>
                      )}
                    </div>
                    {row.last_error.exception_text && (
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-rose-800 bg-white border border-rose-200 rounded px-1.5 py-1"
                           data-testid={`foundation-ar-raw-error-${row.invoice_id}`}>{row.last_error.exception_text}</pre>
                    )}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1">
                {row.push_disabled ? (
                  <button type="button" disabled className="h-7 px-2 text-xs rounded-md border border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed" data-testid={`btn-ar-push-inv-${row.invoice_id}-disabled`}>
                    {data.ar_push_enabled ? "Bridge upgrade required" : "AR push disabled"}
                  </button>
                ) : (
                  <FoundationARPushButton invoice={{ id: row.invoice_id, foundation_ar_status: row.foundation_ar_status }} testIdSuffix={`inv-${row.invoice_id}`}
                                          onPushed={() => reload(q)} />
                )}
                {/* iter311 — diagnostic: runs the real INSERTs and always
                    rolls back, so it can never write to Foundation. */}
                <button
                  type="button"
                  className="h-6 px-1.5 text-[10px] rounded border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50"
                  disabled={dryRunning === row.invoice_id}
                  onClick={() => runDryRun(row.invoice_id)}
                  title="Sends the same header and GL line to Foundation, reads the header back inside the transaction, then rolls back. Writes nothing."
                  data-testid={`btn-ar-dry-run-${row.invoice_id}`}
                >
                  {dryRunning === row.invoice_id ? "Running…" : "Dry run (writes nothing)"}
                </button>
                <button
                  type="button"
                  className="h-6 px-1.5 text-[10px] rounded border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1"
                  disabled={copyingSupport === row.invoice_id}
                  onClick={() => copySupportRequest(row.invoice_id)}
                  title="Copies an email for Foundation support: the exact INSERT statements with literal values, the trigger list and the verbatim SQL error."
                  data-testid={`btn-ar-support-request-${row.invoice_id}`}
                >
                  <Mail className="w-3 h-3" /> {copyingSupport === row.invoice_id ? "Building…" : "Copy support request"}
                </button>
              </div>
            </div>
            {dryRuns[row.invoice_id] && (
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-1.5 py-1"
                   data-testid={`ar-dry-run-result-${row.invoice_id}`}>{JSON.stringify(dryRuns[row.invoice_id], null, 1)}</pre>
            )}
            {supportText[row.invoice_id] && (
              <div className="mt-2" data-testid={`ar-support-request-${row.invoice_id}`}>
                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                  <span>Support request for Foundation (select all + copy if the clipboard was blocked)</span>
                  <button type="button" className="text-blue-600 hover:underline" onClick={() => setSupportText((p) => ({ ...p, [row.invoice_id]: null }))} data-testid={`ar-support-request-close-${row.invoice_id}`}>hide</button>
                </div>
                <textarea readOnly value={supportText[row.invoice_id]} onFocus={(e) => e.target.select()}
                          className="w-full h-48 font-mono text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-1.5 py-1"
                          data-testid={`ar-support-request-text-${row.invoice_id}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}
